export function normalizeWorkerValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

export function getWorkerAccountRole(worker) {
  return normalizeWorkerValue(
    worker?.platformRole ||
      worker?.userRole ||
      worker?.role ||
      ""
  );
}

export function isWorkerAccount(worker) {
  const platformRole = normalizeWorkerValue(
    worker?.platformRole ||
      worker?.userRole ||
      ""
  );

  const accountRole = normalizeWorkerValue(
    worker?.role || ""
  );

  return (
    platformRole === "worker" ||
    accountRole === "worker" ||
    accountRole === "nurse" ||
    accountRole === "caregiver"
  );
}

export function isWorkerAccountActive(worker) {
  const status = normalizeWorkerValue(
    worker?.status || "active"
  );

  return ![
    "inactive",
    "disabled",
    "terminated",
    "archived",
  ].includes(status);
}

export function assertWorkerAccountActive(
  worker
) {
  if (!worker) {
    const error =
      new Error(
        "Worker profile not found"
      );

    error.code =
      "worker_not_found";

    throw error;
  }

  if (
    !isWorkerAccountActive(
      worker
    )
  ) {
    const error =
      new Error(
        "Worker profile not active"
      );

    error.code =
      "worker_not_active";

    throw error;
  }

  return true;
}

export function isOperationallyReadyWorker(
  worker,
  getCompliance
) {
  if (!worker) return false;

  if (!isWorkerAccount(worker)) {
    return false;
  }

  if (!isWorkerAccountActive(worker)) {
    return false;
  }

  const onboardingStatus =
    normalizeWorkerValue(
      worker?.onboardingStatus
    );

  const complianceStatus =
    normalizeWorkerValue(
      worker?.complianceStatus
    );

  const workerStatus =
    normalizeWorkerValue(
      worker?.workerStatus
    );

  const workerOperationalState =
    String(
      worker?.workerOperationalState || ""
    )
      .trim()
      .toUpperCase();

  const compliance =
    typeof getCompliance === "function"
      ? getCompliance(worker)
      : null;

  return (
    compliance?.canWork === true ||
    onboardingStatus === "approved" ||
    workerStatus === "ready" ||
    workerOperationalState === "READY" ||
    complianceStatus === "ready" ||
    complianceStatus === "approved" ||
    worker?.onboardingCompleted === true ||
    worker?.readyForShifts === true
  );
}

export function sortWorkersByDisplayName(
  workers = []
) {
  return [...workers].sort(
    (a, b) =>
      String(
        a?.displayName ||
          a?.fullName ||
          a?.email ||
          a?.uid ||
          ""
      ).localeCompare(
        String(
          b?.displayName ||
            b?.fullName ||
            b?.email ||
            b?.uid ||
            ""
        )
      )
  );
}

export function getOperationalWorkers({
  workers = [],
  getCompliance,
} = {}) {
  const rows =
    Array.isArray(workers)
      ? workers
      : [];

  return sortWorkersByDisplayName(
    rows.filter((worker) =>
      isOperationallyReadyWorker(
        worker,
        getCompliance
      )
    )
  );
}

/* ─────────────────────────────
   ROLE / CREDENTIAL ELIGIBILITY
───────────────────────────── */

export function normalizeWorkerRole(
  value
) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function normalizeWorkerCredentials(
  credentials
) {
  const rows =
    Array.isArray(credentials)
      ? credentials
      : [];

  return rows
    .map((credential) => {
      if (
        credential &&
        typeof credential ===
          "object"
      ) {
        return {
          type:
            credential.type ||
            credential.credential ||
            "",

          verified:
            credential.verified ===
            true,

          expiresAt:
            credential.expiresAt ??
            null,
        };
      }

      if (
        typeof credential ===
        "string"
      ) {
        return {
          type: credential,
          verified: true,
          expiresAt: null,
        };
      }

      return null;
    })
    .filter(Boolean);
}

function isWorkerCredentialCurrent(
  credential,
  nowMs = Date.now()
) {
  if (
    !credential?.expiresAt
  ) {
    return true;
  }

  try {
    const expiresAtMs =
      typeof credential
        .expiresAt?.toDate ===
      "function"
        ? credential
            .expiresAt
            .toDate()
            .getTime()
        : new Date(
            credential.expiresAt
          ).getTime();

    if (
      !Number.isFinite(
        expiresAtMs
      )
    ) {
      return true;
    }

    return (
      expiresAtMs > nowMs
    );
  } catch {
    return true;
  }
}

export function hasValidCredentialForRole(
  worker,
  requiredRole,
  nowMs = Date.now()
) {
  if (!worker) {
    return false;
  }

  const requiredKey =
    normalizeWorkerRole(
      requiredRole
    );

  if (!requiredKey) {
    return false;
  }

  const credentials =
    normalizeWorkerCredentials(
      worker?.credentials
    );

  const verifiedCredential =
    credentials.some(
      (credential) => {
        const credentialKey =
          normalizeWorkerRole(
            credential?.type
          );

        return (
          credentialKey ===
            requiredKey &&
          credential?.verified ===
            true &&
          isWorkerCredentialCurrent(
            credential,
            nowMs
          )
        );
      }
    );

  if (verifiedCredential) {
    return true;
  }

  /*
   * Legacy / transitional records may store
   * approved worker roles separately from
   * the credentials array.
   */
  const approvedPositions =
    Array.isArray(
      worker?.approvedPositions
    )
      ? worker.approvedPositions.map(
          normalizeWorkerRole
        )
      : [];

  const positions =
    Array.isArray(
      worker?.positions
    )
      ? worker.positions.map(
          normalizeWorkerRole
        )
      : [];

  const roleFields = [
    worker?.primaryRole,
    worker?.workerType,
    worker?.roleType,
    worker?.licenseType,
    worker?.position,
  ].map(
    normalizeWorkerRole
  );

  const professionalLicenseStatus =
    normalizeWorkerValue(
      worker?.credentialMap
        ?.professional_license
        ?.status
    );

  const professionalLicenseApproved =
    professionalLicenseStatus ===
    "approved";

  const roleMatches =
    approvedPositions.includes(
      requiredKey
    ) ||
    positions.includes(
      requiredKey
    ) ||
    roleFields.includes(
      requiredKey
    );

  return (
    professionalLicenseApproved &&
    roleMatches
  );
}

/* ─────────────────────────────
   SHIFT WORK ELIGIBILITY
───────────────────────────── */

export function validateWorkerForShift({
  worker,
  requiredRole,
  getCompliance,
  nowMs = Date.now(),
} = {}) {
  if (!worker) {
    return {
      valid: false,
      code: "missing_worker",
      message:
        "Worker profile not found",
    };
  }

  if (
    !isOperationallyReadyWorker(
      worker,
      getCompliance
    )
  ) {
    return {
      valid: false,
      code:
        "worker_not_ready",
      message:
        "Worker is not operationally ready",
    };
  }

  if (
    !hasValidCredentialForRole(
      worker,
      requiredRole,
      nowMs
    )
  ) {
    return {
      valid: false,
      code:
        "credential_invalid",
      message:
        `Worker does not have a valid credential for ${requiredRole}`,
    };
  }

  return {
    valid: true,
    code: "",
    message: "",
  };
}

export function assertWorkerEligibleForShift(
  args
) {
  const result =
    validateWorkerForShift(
      args
    );

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}